import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CPInput } from "../CPInput";
import {
  activateClassification,
  createClassification,
  deactivateClassification,
  listClassifications,
  setDefaultClassification,
  updateClassification,
} from "../../helpers/catalogs/classificationCatalog";
import { usePermissions } from "../../hooks/usePermissions";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { DeskEmpty, DeskPill, DeskTable } from "../desktop/DeskTable";
import { AdmMoveButtons } from "./desktop/AdmKit";

export function AdminClassificationsScreen({ onBack }) {
  const { actor } = usePermissions();
  const isDesktop = useIsDesktopViewport();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const loadItems = useCallback(async () => {
    if (!actor) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const rows = await listClassifications(supabase, {
        activeOnly: false,
        actor,
      });
      setItems(rows);
    } catch (err) {
      console.error("LOAD CLASSIFICATIONS ERROR:", err);
      setError("Unable to load classifications.");
    } finally {
      setLoading(false);
    }
  }, [actor]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleAdd = async () => {
    setError("");
    try {
      const nextOrder =
        items.reduce(
          (max, item) => Math.max(max, Number(item.display_order || 0)),
          0,
        ) + 1;

      await createClassification(
        {
          name: newName,
          display_order: nextOrder,
          is_default: items.length === 0,
        },
        supabase,
        actor,
      );
      setNewName("");
      await loadItems();
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("ADD CLASSIFICATION ERROR:", err);
      setError(err.message || "Failed to add classification.");
    }
  };

  const handleToggleActive = async (item) => {
    setError("");
    try {
      if (item.is_active) {
        await deactivateClassification(item.classification_id, supabase, actor);
      } else {
        await activateClassification(item.classification_id, supabase, actor);
      }
      await loadItems();
    } catch (err) {
      console.error("TOGGLE CLASSIFICATION ERROR:", err);
      setError(err.message || "Failed to update classification.");
    }
  };

  const handleSetDefault = async (item) => {
    setError("");
    try {
      await setDefaultClassification(item.classification_id, supabase, actor);
      await loadItems();
    } catch (err) {
      console.error("DEFAULT CLASSIFICATION ERROR:", err);
      setError(err.message || "Failed to set default classification.");
    }
  };

  const handleMove = async (item, direction) => {
    setError("");
    const sorted = [...items].sort(
      (a, b) => Number(a.display_order) - Number(b.display_order),
    );
    const index = sorted.findIndex(
      (row) => row.classification_id === item.classification_id,
    );
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= sorted.length) {
      return;
    }

    const other = sorted[swapIndex];

    try {
      await Promise.all([
        updateClassification(
          item.classification_id,
          { display_order: other.display_order },
          supabase,
          actor,
        ),
        updateClassification(
          other.classification_id,
          { display_order: item.display_order },
          supabase,
          actor,
        ),
      ]);
      await loadItems();
    } catch (err) {
      console.error("REORDER CLASSIFICATION ERROR:", err);
      setError(err.message || "Failed to reorder classifications.");
    }
  };

  if (isDesktop) {
    const sorted = [...items].sort(
      (a, b) => Number(a.display_order) - Number(b.display_order),
    );
    return (
      <CrewPilotModuleShell
        title="Classifications"
        subtitle="Organization classification catalog"
        sectionLabel="Payroll"
        onBack={onBack}
        layout={PAGE_LAYOUT.COMMAND}
      >
        <div className="adm-main-side">
          {loading ? (
            <p className="adm-note">Loading…</p>
          ) : items.length === 0 ? (
            <DeskEmpty title="No classifications found." />
          ) : (
            <DeskTable
              caption="Classifications"
              rows={sorted}
              rowKey={(item) => item.classification_id}
              rowClassName={(item) => (item.is_active ? "" : "is-muted")}
              columns={[
                {
                  key: "order",
                  label: "Order",
                  width: "104px",
                  render: (item) => (
                    <AdmMoveButtons
                      name={item.name}
                      onUp={() => handleMove(item, -1)}
                      onDown={() => handleMove(item, 1)}
                    />
                  ),
                },
                {
                  key: "name",
                  label: "Classification",
                  render: (item) => <span className="dt-strong">{item.name}</span>,
                },
                {
                  key: "status",
                  label: "Status",
                  width: "190px",
                  render: (item) => (
                    <span className="adm-rowact" style={{ justifyContent: "flex-start" }}>
                      <DeskPill tone={item.is_active ? "green" : "muted"}>
                        {item.is_active ? "Active" : "Inactive"}
                      </DeskPill>
                      {item.is_default ? <DeskPill tone="gold">Default</DeskPill> : null}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  label: "Actions",
                  align: "right",
                  width: "250px",
                  render: (item) => (
                    <span className="adm-rowact">
                      {!item.is_default && item.is_active && (
                        <button
                          type="button"
                          className="kpi-back-btn adm-sm"
                          onClick={() => handleSetDefault(item)}
                        >
                          Set default
                        </button>
                      )}
                      <button
                        type="button"
                        className={`kpi-back-btn adm-sm${item.is_active ? " adm-soft" : ""}`}
                        onClick={() => handleToggleActive(item)}
                      >
                        {item.is_active ? "Deactivate" : "Activate"}
                      </button>
                    </span>
                  ),
                },
              ]}
            />
          )}

          <div className="rb-card">
            <div className="section-heading">ADD CLASSIFICATION</div>
            <CPInput label="Name" value={newName} onChange={setNewName} />
            <div className="adm-actions adm-actions--plain">
              <button type="button" className="kpi-back-btn adm-primary" onClick={handleAdd}>
                Add classification
              </button>
            </div>
            {error && (
              <p className="admin-placeholder-note" style={{ marginTop: "12px" }}>
                {error}
              </p>
            )}
            {saved && (
              <p className="admin-save-confirmation" role="status" style={{ marginTop: "12px" }}>
                Classification catalog updated.
              </p>
            )}
          </div>
        </div>
      </CrewPilotModuleShell>
    );
  }

  return (
    <CrewPilotModuleShell
      title="Classifications"
      subtitle="Organization classification catalog"
      sectionLabel="Payroll"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
    >
      <div className="rb-card">
        <div className="section-heading">ADD CLASSIFICATION</div>

        <CPInput label="Name" value={newName} onChange={setNewName} />

        <button type="button" className="kpi-back-btn" onClick={handleAdd}>
          Add classification
        </button>

        {error && (
          <p className="admin-placeholder-note" style={{ marginTop: "12px" }}>
            {error}
          </p>
        )}

        {saved && (
          <p className="admin-save-confirmation" role="status">
            Classification catalog updated.
          </p>
        )}
      </div>

      <div className="rb-card">
        <div className="section-heading">CLASSIFICATIONS</div>

        {loading ? (
          <p className="admin-placeholder-note">Loading…</p>
        ) : items.length === 0 ? (
          <p className="admin-placeholder-note">No classifications found.</p>
        ) : (
          items.map((item) => (
            <div
              key={item.classification_id}
              className="admin-toggle-row"
              style={{ justifyContent: "space-between" }}
            >
              <div>
                <strong className="admin-data-primary">{item.name}</strong>
                {item.is_default ? (
                  <span className="admin-data-secondary"> (default)</span>
                ) : null}
                {!item.is_active ? (
                  <span className="admin-data-secondary"> — inactive</span>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="kpi-back-btn"
                  onClick={() => handleMove(item, -1)}
                >
                  Up
                </button>
                <button
                  type="button"
                  className="kpi-back-btn"
                  onClick={() => handleMove(item, 1)}
                >
                  Down
                </button>
                {!item.is_default && item.is_active && (
                  <button
                    type="button"
                    className="kpi-back-btn"
                    onClick={() => handleSetDefault(item)}
                  >
                    Set default
                  </button>
                )}
                <button
                  type="button"
                  className="kpi-back-btn"
                  onClick={() => handleToggleActive(item)}
                >
                  {item.is_active ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </CrewPilotModuleShell>
  );
}
